/*
  Rolls the drop colour across the whole page as the section that carries it
  comes up, and back to the page beige as that section leaves.

  Two halves of a contract:

  - One section is the trigger. It declares the journey with data-wash-from /
    data-wash-to / data-wash-ink-from / data-wash-ink-to.
  - Every opaque surface that should travel with it carries data-colour-wash
    and reads its own colours as var(--wash-bg, <rest>) and
    var(--wash-ink, <rest>). A hairline drawn on such a surface reads
    var(--wash-rule, <rest>), which is only ever written when the drop is dark
    enough for the page's warm brown to vanish into it. Nothing else is
    touched.

  The page background is written straight onto <body>, which is what makes this
  read as the screen changing colour rather than a band scrolling past it. It
  deliberately leaves --color-background alone: base.css hangs cards, inputs and
  drawers off that variable, and moving it would recolour all of them.

  With no JavaScript, or with reduced motion, every surface falls back to its
  own resting colour and the trigger section simply stands in its drop colour.
*/
(function () {
  function toRgb(value) {
    var hex = (value || '').trim().replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    var n = parseInt(hex, 16);
    if (isNaN(n) || hex.length !== 6) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  var trigger = null;
  var from, to, inkFrom, inkTo, start;

  /* Collected from the whole document rather than from the trigger's siblings,
     so the sticky header — which lives in its own section group — travels with
     the page, and so reordering the homepage cannot break the effect. */
  var others = [];
  function collect() {
    others = [];
    var all = document.querySelectorAll('[data-colour-wash]');
    for (var i = 0; i < all.length; i++) {
      if (all[i] !== trigger) others.push(all[i]);
    }
  }

  /*
    The section that declares the journey is looked up again whenever the page
    changes under us, rather than held from the start. The theme editor — and
    the development server — rebuild a section by replacing its markup outright,
    and an element kept from before that is no longer in the page: the wash goes
    on painting it, and the page stops changing colour with no error to show for
    it. Its colours are re-read at the same time, since they are the settings
    that were most likely just edited.
  */
  var searched = false;

  function resolve() {
    searched = true;
    trigger = document.querySelector('[data-wash-to]');
    if (!trigger) return false;

    from = toRgb(trigger.dataset.washFrom);
    to = toRgb(trigger.dataset.washTo);
    inkFrom = toRgb(trigger.dataset.washInkFrom);
    inkTo = toRgb(trigger.dataset.washInkTo);

    /* Where the section has to be for the colour to start moving, as a share
       of the screen measured from the top: 1 is the very bottom of the screen,
       which is where it starts unless the section asks for later. A section
       that follows something worth finishing reading — the statement — says so
       with data-wash-start, so the colour waits for it to clear the middle. */
    start = parseFloat(trigger.dataset.washStart);
    if (!(start > 0 && start <= 1)) start = 1;

    if (!from || !to || !inkFrom || !inkTo) {
      trigger = null;
      return false;
    }

    collect();
    return true;
  }

  function ready() {
    /* Held while it is still in the page, looked up again the moment it is not.
       On the pages that have no wash at all the search is made once and not
       repeated on every frame of every scroll — a section arriving later says
       so itself, through shopify:section:load. */
    if (trigger) return document.contains(trigger) || resolve();
    return searched ? false : resolve();
  }

  function mix(a, b, t) {
    return [
      Math.round(a[0] + (b[0] - a[0]) * t),
      Math.round(a[1] + (b[1] - a[1]) * t),
      Math.round(a[2] + (b[2] - a[2]) * t)
    ];
  }

  function css(rgb) {
    return 'rgb(' + rgb[0] + ', ' + rgb[1] + ', ' + rgb[2] + ')';
  }

  function luminance(rgb) {
    var a = [];
    for (var i = 0; i < 3; i++) {
      var v = rgb[i] / 255;
      a[i] = v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  }

  function contrast(a, b) {
    var l1 = luminance(a);
    var l2 = luminance(b);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  }

  /*
    The ink is picked, not blended. Fading the ink from dark to light in step
    with the background sends both through the middle together and the text
    disappears — measured at 1.3:1 halfway through a beige-to-purple wash.
    Choosing whichever of the two inks reads better against the background as
    it stands keeps the worst moment at the crossover, where by definition both
    are equally legible, and the ink flips once rather than dissolving. When a
    drop is pale enough that both ends want dark ink, nothing flips at all.
  */
  function inkFor(bg) {
    return contrast(bg, inkFrom) >= contrast(bg, inkTo) ? inkFrom : inkTo;
  }

  /*
    Smoothstep: eases the colour in and out at the extremes and crosses the
    muddy middle at its fastest, so the least legible stretch is also the
    briefest.
  */
  function smooth(t) {
    return t * t * (3 - 2 * t);
  }

  /*
    The perceived brightness Shopify's own color_brightness filter returns, so
    the decision made here and the ones made in Liquid agree on every drop.
  */
  function brightness(rgb) {
    return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  }

  function paint(el, bgRgb) {
    var inkRgb = inkFor(bgRgb);
    el.style.setProperty('--wash-bg', css(bgRgb));
    el.style.setProperty('--wash-ink', css(inkRgb));

    /*
      The hairlines the page draws are a warm brown, which reads on beige and
      disappears on a dark drop. So they follow the words: the moment the ink
      turns lighter than what it is written on, the rules drawn on that surface
      turn with it, in the same ink at the weight a hairline is drawn with.
      Asked of the ink that was just chosen rather than of the background on
      its own, so the two can never disagree for a frame at the crossover. On a
      pale drop nothing is said and each rule keeps the brown it was drawn in.
    */
    if (brightness(inkRgb) > brightness(bgRgb)) {
      el.style.setProperty('--wash-rule', 'rgba(' + inkRgb.join(', ') + ', 0.5)');
    } else {
      el.style.removeProperty('--wash-rule');
    }
  }

  function clear(el) {
    el.style.removeProperty('--wash-bg');
    el.style.removeProperty('--wash-ink');
    el.style.removeProperty('--wash-rule');
  }

  function apply(amount) {
    var bgRgb = mix(from, to, smooth(amount));

    /* The trigger always holds the current value: it is the one surface whose
       resting colour is the drop colour rather than the page beige. */
    paint(trigger, bgRgb);

    if (amount === 0) {
      /* Off screen entirely — hand every other surface back to its own
         settings instead of holding a colour this section wrote. */
      for (var i = 0; i < others.length; i++) clear(others[i]);
      document.body.style.removeProperty('background-color');
      return;
    }

    for (var j = 0; j < others.length; j++) paint(others[j], bgRgb);
    document.body.style.backgroundColor = css(bgRgb);
  }

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  function update() {
    if (!ready()) return;

    if (reduced.matches) {
      /* No wash: the section stands in its drop colour and the rest of the
         page keeps its own. */
      paint(trigger, to);
      for (var i = 0; i < others.length; i++) clear(others[i]);
      document.body.style.removeProperty('background-color');
      return;
    }

    var box = trigger.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    /* The colour settles over 45% of a screen of the section's approach, so it
       is done before the trigger's own text reaches a comfortable reading
       height. Only the way in waits: on the way out the colour hands back to
       the page beige as the section leaves, as it always did. */
    var travel = vh * 0.45;
    var arriving = clamp((vh * start - box.top) / travel);
    var leaving = clamp(box.bottom / travel);
    apply(Math.min(arriving, leaving));
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      update();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  if (reduced.addEventListener) reduced.addEventListener('change', update);
  document.addEventListener('shopify:section:load', function () {
    resolve();
    onScroll();
  });
  update();
})();
